import { Controller } from "@hotwired/stimulus"
import paper from "paper"

/**
 * Outlet Flow Editor - n8n-inspired visual node editor for action outlets
 * 
 * Uses Paper.js for canvas rendering with:
 * - Grid background with snapping
 * - Draggable node cards (action + outlets)
 * - Bezier curve cables connecting nodes
 * - Click-to-add outlet workflow
 */
export default class extends Controller {
  static targets = [
    "canvas",
    "outletsData",
    "chainsData", 
    "panel",
    "panelContent",
    "addMenu"
  ]

  static values = {
    actionId: Number,
    actionName: String,
    channel: String,
    outlets: Array,
    chains: Array,
    availableActions: Array,
    gridSize: { type: Number, default: 20 },
    nodeWidth: { type: Number, default: 200 },
    nodeHeight: { type: Number, default: 80 }
  }

  connect() {
    console.log('[OutletFlowEditor] Controller connected')
    console.log('[OutletFlowEditor] actionId:', this.actionIdValue)
    console.log('[OutletFlowEditor] actionName:', this.actionNameValue)
    console.log('[OutletFlowEditor] outlets:', this.outletsValue)
    console.log('[OutletFlowEditor] outlets count:', this.outletsValue?.length)
    
    this.setupPaper()
    this.drawGrid()
    this.renderNodes()
    this.setupEventHandlers()
  }

  disconnect() {
    if (this.paperScope) {
      this.paperScope.remove()
    }
  }

  setupPaper() {
    // Create a new paper scope for this canvas
    this.paperScope = new paper.PaperScope()
    this.paperScope.setup(this.canvasTarget)
    
    // Store reference for event handlers
    this.project = this.paperScope.project
    this.view = this.paperScope.view
    
    // Layer organization
    this.gridLayer = new this.paperScope.Layer({ name: 'grid' })
    this.cablesLayer = new this.paperScope.Layer({ name: 'cables' })
    this.nodesLayer = new this.paperScope.Layer({ name: 'nodes' })
    
    // Track nodes and cables
    this.nodes = new Map()
    this.cables = []
    this.selectedNode = null
    this.dragOffset = null
  }

  drawGrid() {
    this.gridLayer.activate()
    
    const bounds = this.view.bounds
    const gridSize = this.gridSizeValue
    
    // Draw dot grid (like n8n)
    for (let x = 0; x < bounds.width; x += gridSize) {
      for (let y = 0; y < bounds.height; y += gridSize) {
        new this.paperScope.Path.Circle({
          center: [x, y],
          radius: 1,
          fillColor: '#e5e7eb'
        })
      }
    }
    
    this.nodesLayer.activate()
  }

  renderNodes() {
    this.nodesLayer.activate()
    
    // Clear existing nodes
    this.nodesLayer.removeChildren()
    this.nodes.clear()
    
    // Main action node (source) - positioned top-left region
    const actionNode = this.createActionNode(
      this.actionNameValue || 'Action',
      new this.paperScope.Point(40, 60)
    )
    this.nodes.set('action', actionNode)
    
    // Outlet nodes - positioned to the right
    const outlets = this.outletsValue || []
    outlets.forEach((outlet, index) => {
      const x = 400
      const y = 100 + (index * 120)
      const outletNode = this.createOutletNode(outlet, new this.paperScope.Point(x, y))
      this.nodes.set(`outlet-${outlet.id || index}`, outletNode)
    })
    
    // Draw cables after nodes
    this.renderCables()
  }

  createActionNode(name, position) {
    const group = new this.paperScope.Group()
    group.data = { type: 'action', name }
    
    // Card background
    const card = new this.paperScope.Path.Rectangle({
      point: position,
      size: [this.nodeWidthValue, this.nodeHeightValue],
      radius: 8,
      fillColor: '#1f2937',
      shadowColor: new this.paperScope.Color(0, 0, 0, 0.15),
      shadowBlur: 10,
      shadowOffset: new this.paperScope.Point(0, 4)
    })
    group.addChild(card)
    
    // Icon area
    const iconBg = new this.paperScope.Path.Rectangle({
      point: [position.x + 12, position.y + 12],
      size: [40, 40],
      radius: 6,
      fillColor: '#7c3aed'
    })
    group.addChild(iconBg)
    
    // Slack icon (simplified)
    const icon = new this.paperScope.PointText({
      point: [position.x + 24, position.y + 38],
      content: '⚡',
      fontSize: 18,
      fillColor: 'white'
    })
    group.addChild(icon)
    
    // Title
    const title = new this.paperScope.PointText({
      point: [position.x + 62, position.y + 30],
      content: this.truncateText(name, 16),
      fontSize: 13,
      fontWeight: 'bold',
      fillColor: 'white'
    })
    group.addChild(title)
    
    // Subtitle - show channel alias
    const channelText = this.channelValue ? `#${this.channelValue}` : 'Slack Message'
    const subtitle = new this.paperScope.PointText({
      point: [position.x + 62, position.y + 48],
      content: channelText,
      fontSize: 11,
      fillColor: '#9ca3af'
    })
    group.addChild(subtitle)
    
    // Output connector (right side)
    const connector = new this.paperScope.Path.Circle({
      center: [position.x + this.nodeWidthValue, position.y + this.nodeHeightValue / 2],
      radius: 8,
      fillColor: '#10b981',
      strokeColor: 'white',
      strokeWidth: 2
    })
    connector.data = { type: 'output', nodeId: 'action' }
    group.addChild(connector)
    
    // Store bounds for dragging
    group.data.bounds = card.bounds.clone()
    group.data.connector = connector
    
    return group
  }

  createOutletNode(outlet, position) {
    const group = new this.paperScope.Group()
    group.data = { 
      type: 'outlet', 
      outlet,
      id: outlet.id || `new-${Date.now()}`
    }
    
    // Determine style based on outlet type
    const styles = {
      button: { color: '#3b82f6', icon: '🔘' },
      select: { color: '#8b5cf6', icon: '📋' },
      checkbox: { color: '#06b6d4', icon: '☑️' },
      radio: { color: '#f59e0b', icon: '⭕' }
    }
    const style = styles[outlet.outlet_type] || styles.button
    
    // Card background
    const card = new this.paperScope.Path.Rectangle({
      point: position,
      size: [this.nodeWidthValue, this.nodeHeightValue + 20],
      radius: 8,
      fillColor: 'white',
      strokeColor: '#e5e7eb',
      strokeWidth: 1,
      shadowColor: new this.paperScope.Color(0, 0, 0, 0.1),
      shadowBlur: 8,
      shadowOffset: new this.paperScope.Point(0, 2)
    })
    group.addChild(card)
    
    // Input connector (left side)
    const inputConnector = new this.paperScope.Path.Circle({
      center: [position.x, position.y + (this.nodeHeightValue + 20) / 2],
      radius: 8,
      fillColor: '#10b981',
      strokeColor: 'white',
      strokeWidth: 2
    })
    inputConnector.data = { type: 'input', nodeId: group.data.id }
    group.addChild(inputConnector)
    
    // Type badge
    const badge = new this.paperScope.Path.Rectangle({
      point: [position.x + 12, position.y + 12],
      size: [40, 24],
      radius: 4,
      fillColor: style.color
    })
    group.addChild(badge)
    
    const badgeText = new this.paperScope.PointText({
      point: [position.x + 22, position.y + 28],
      content: style.icon,
      fontSize: 12
    })
    group.addChild(badgeText)
    
    // Label
    const label = new this.paperScope.PointText({
      point: [position.x + 62, position.y + 28],
      content: this.truncateText(outlet.label || 'Button', 14),
      fontSize: 13,
      fontWeight: 'bold',
      fillColor: '#1f2937'
    })
    group.addChild(label)
    
    // Value
    const value = new this.paperScope.PointText({
      point: [position.x + 62, position.y + 46],
      content: `Value: ${this.truncateText(outlet.value || 'click', 12)}`,
      fontSize: 10,
      fillColor: '#6b7280'
    })
    group.addChild(value)
    
    // Style indicator for buttons
    if (outlet.outlet_type === 'button' && outlet.style) {
      const styleColors = { primary: '#22c55e', danger: '#ef4444' }
      const styleIndicator = new this.paperScope.Path.Circle({
        center: [position.x + this.nodeWidthValue - 20, position.y + 20],
        radius: 6,
        fillColor: styleColors[outlet.style] || '#9ca3af'
      })
      group.addChild(styleIndicator)
    }
    
    // Chain indicator (output connector) if has chain
    if (outlet.chain) {
      const outputConnector = new this.paperScope.Path.Circle({
        center: [position.x + this.nodeWidthValue, position.y + (this.nodeHeightValue + 20) / 2],
        radius: 8,
        fillColor: '#f59e0b',
        strokeColor: 'white',
        strokeWidth: 2
      })
      outputConnector.data = { type: 'chain-output', nodeId: group.data.id }
      group.addChild(outputConnector)
      group.data.chainConnector = outputConnector
    }
    
    // Edit button
    const editBtn = new this.paperScope.Path.Rectangle({
      point: [position.x + 12, position.y + this.nodeHeightValue - 8],
      size: [60, 24],
      radius: 4,
      fillColor: '#f3f4f6'
    })
    editBtn.data = { action: 'edit', nodeId: group.data.id }
    group.addChild(editBtn)
    
    const editText = new this.paperScope.PointText({
      point: [position.x + 28, position.y + this.nodeHeightValue + 6],
      content: 'Edit',
      fontSize: 10,
      fillColor: '#4b5563'
    })
    group.addChild(editText)
    
    // Delete button
    const deleteBtn = new this.paperScope.Path.Rectangle({
      point: [position.x + 80, position.y + this.nodeHeightValue - 8],
      size: [60, 24],
      radius: 4,
      fillColor: '#fef2f2'
    })
    deleteBtn.data = { action: 'delete', nodeId: group.data.id }
    group.addChild(deleteBtn)
    
    const deleteText = new this.paperScope.PointText({
      point: [position.x + 92, position.y + this.nodeHeightValue + 6],
      content: 'Delete',
      fontSize: 10,
      fillColor: '#dc2626'
    })
    group.addChild(deleteText)
    
    // Store references
    group.data.bounds = card.bounds.clone()
    group.data.inputConnector = inputConnector
    
    return group
  }

  renderCables() {
    this.cablesLayer.activate()
    this.cablesLayer.removeChildren()
    this.cables = []
    
    const actionNode = this.nodes.get('action')
    if (!actionNode) return
    
    const actionConnector = actionNode.data.connector
    
    // Draw cable from action to each outlet
    this.nodes.forEach((node, key) => {
      if (key === 'action') return
      if (node.data.type !== 'outlet') return
      
      const inputConnector = node.data.inputConnector
      if (!inputConnector) return
      
      const cable = this.createCable(
        actionConnector.position,
        inputConnector.position
      )
      this.cables.push(cable)
    })
    
    this.nodesLayer.activate()
  }

  createCable(from, to) {
    this.cablesLayer.activate()
    
    // Calculate control points for smooth bezier curve
    const dx = to.x - from.x
    const controlOffset = Math.min(Math.abs(dx) * 0.5, 100)
    
    const path = new this.paperScope.Path({
      strokeColor: '#10b981',
      strokeWidth: 3,
      strokeCap: 'round'
    })
    
    path.add(from)
    path.cubicCurveTo(
      new this.paperScope.Point(from.x + controlOffset, from.y),
      new this.paperScope.Point(to.x - controlOffset, to.y),
      to
    )
    
    // Add animated dash (optional, for visual flair)
    // path.dashArray = [10, 5]
    
    this.nodesLayer.activate()
    return path
  }

  setupEventHandlers() {
    const tool = new this.paperScope.Tool()
    
    tool.onMouseDown = (event) => {
      const hitResult = this.project.hitTest(event.point, {
        fill: true,
        stroke: true,
        tolerance: 5
      })
      
      if (hitResult && hitResult.item) {
        const item = this.findParentGroup(hitResult.item)
        
        // Check for button actions
        if (hitResult.item.data?.action === 'edit') {
          this.editOutlet(hitResult.item.data.nodeId)
          return
        }
        if (hitResult.item.data?.action === 'delete') {
          this.deleteOutlet(hitResult.item.data.nodeId)
          return
        }
        
        // Start dragging
        if (item && item.data?.type) {
          this.selectedNode = item
          this.dragOffset = event.point.subtract(item.bounds.topLeft)
          item.bringToFront()
        }
      }
    }
    
    tool.onMouseDrag = (event) => {
      if (!this.selectedNode) return
      
      // Calculate new position with grid snapping
      let newPos = event.point.subtract(this.dragOffset)
      newPos = this.snapToGrid(newPos)
      
      // Move the node
      const delta = newPos.subtract(this.selectedNode.bounds.topLeft)
      this.selectedNode.translate(delta)
      
      // Update cables
      this.renderCables()
    }
    
    tool.onMouseUp = () => {
      if (this.selectedNode) {
        // Update outlet position in data
        this.updateOutletPosition(this.selectedNode)
        this.selectedNode = null
      }
      this.dragOffset = null
    }
  }

  findParentGroup(item) {
    let current = item
    while (current) {
      if (current instanceof this.paperScope.Group && current.data?.type) {
        return current
      }
      current = current.parent
    }
    return null
  }

  snapToGrid(point) {
    const grid = this.gridSizeValue
    return new this.paperScope.Point(
      Math.round(point.x / grid) * grid,
      Math.round(point.y / grid) * grid
    )
  }

  truncateText(text, maxLength) {
    if (!text) return ''
    return text.length > maxLength ? text.slice(0, maxLength) + '...' : text
  }

  // === Actions ===

  addOutlet(event) {
    const type = event.currentTarget.dataset.outletType || 'button'
    
    // Calculate position for new outlet
    const existingOutlets = this.outletsValue || []
    const y = 100 + (existingOutlets.length * 120)
    
    const newOutlet = {
      id: `new-${Date.now()}`,
      outlet_type: type,
      label: this.getDefaultLabel(type),
      value: this.getDefaultValue(type),
      style: type === 'button' ? 'primary' : null,
      position: existingOutlets.length,
      active: true,
      config: {},
      _isNew: true,
      _x: 400,
      _y: y
    }
    
    // Update outlets value
    this.outletsValue = [...existingOutlets, newOutlet]
    
    // Sync to form so the hidden input is updated
    this.syncToForm()
    
    // Hide empty state overlay
    this.hideEmptyState()
    
    // Re-render
    this.renderNodes()
    
    // Open edit panel for new outlet
    this.editOutlet(newOutlet.id)
    
    // Hide add menu
    this.hideAddMenu()
  }

  getDefaultLabel(type) {
    const defaults = {
      button: 'Click Me',
      select: 'Choose Option',
      checkbox: 'Select Items',
      radio: 'Pick One'
    }
    return defaults[type] || 'Action'
  }

  getDefaultValue(type) {
    const defaults = {
      button: 'clicked',
      select: 'selected',
      checkbox: 'checked',
      radio: 'chosen'
    }
    return defaults[type] || 'value'
  }

  editOutlet(nodeId) {
    const outlets = this.outletsValue || []
    const outlet = outlets.find(o => (o.id || `new-${o._tempId}`) === nodeId)
    
    if (!outlet) return
    
    this.openPanel(outlet)
  }

  deleteOutlet(nodeId) {
    if (!confirm('Delete this outlet?')) return
    
    const outlets = this.outletsValue || []
    this.outletsValue = outlets.filter(o => (o.id || `new-${o._tempId}`) !== nodeId)
    
    this.renderNodes()
    this.syncToForm()
  }

  updateOutletPosition(node) {
    if (node.data?.type !== 'outlet') return
    
    const outlets = this.outletsValue || []
    const outletId = node.data.id
    
    this.outletsValue = outlets.map(o => {
      if ((o.id || `new-${o._tempId}`) === outletId) {
        return {
          ...o,
          _x: node.bounds.x,
          _y: node.bounds.y
        }
      }
      return o
    })
  }

  // === Panel ===

  openPanel(outlet) {
    if (!this.hasPanelTarget) return
    
    this.currentOutlet = outlet
    this.panelContentTarget.innerHTML = this.renderPanelContent(outlet)
    this.panelTarget.classList.remove('translate-x-full')
    this.panelTarget.classList.add('translate-x-0')
  }

  closePanel() {
    if (!this.hasPanelTarget) return
    
    this.panelTarget.classList.remove('translate-x-0')
    this.panelTarget.classList.add('translate-x-full')
    this.currentOutlet = null
  }

  renderPanelContent(outlet) {
    const isButton = outlet.outlet_type === 'button'
    const hasOptions = ['select', 'checkbox', 'radio'].includes(outlet.outlet_type)
    
    return `
      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Label</label>
          <input type="text" 
                 data-field="label" 
                 value="${outlet.label || ''}"
                 data-action="input->outlet-flow-editor#updateField"
                 class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Value</label>
          <input type="text" 
                 data-field="value" 
                 value="${outlet.value || ''}"
                 data-action="input->outlet-flow-editor#updateField"
                 class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
          <p class="text-xs text-gray-500 mt-1">Returned when user interacts</p>
        </div>
        
        ${isButton ? `
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Style</label>
          <select data-field="style" 
                  data-action="change->outlet-flow-editor#updateField"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
            <option value="" ${!outlet.style ? 'selected' : ''}>Default</option>
            <option value="primary" ${outlet.style === 'primary' ? 'selected' : ''}>Primary (Green)</option>
            <option value="danger" ${outlet.style === 'danger' ? 'selected' : ''}>Danger (Red)</option>
          </select>
        </div>
        ` : ''}
        
        ${hasOptions ? `
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Options</label>
          <div data-options-list class="space-y-2">
            ${(outlet.config?.options || []).map((opt, i) => `
              <div class="flex gap-2">
                <input type="text" placeholder="Text" value="${opt.text || ''}" 
                       data-option-index="${i}" data-option-field="text"
                       data-action="input->outlet-flow-editor#updateOption"
                       class="flex-1 px-2 py-1 border border-gray-300 rounded text-sm">
                <input type="text" placeholder="Value" value="${opt.value || ''}"
                       data-option-index="${i}" data-option-field="value"
                       data-action="input->outlet-flow-editor#updateOption"
                       class="flex-1 px-2 py-1 border border-gray-300 rounded text-sm">
                <button type="button" data-action="click->outlet-flow-editor#removeOption" data-index="${i}"
                        class="px-2 text-red-500 hover:text-red-700">×</button>
              </div>
            `).join('')}
          </div>
          <button type="button" data-action="click->outlet-flow-editor#addOption"
                  class="mt-2 text-sm text-blue-600 hover:text-blue-800">+ Add Option</button>
        </div>
        ` : ''}
        
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Response Message</label>
          <textarea data-field="response_message"
                    data-action="input->outlet-flow-editor#updateField"
                    rows="2"
                    placeholder="Message shown after interaction. Use {{interaction.user.name}}"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500">${outlet.response_message || ''}</textarea>
        </div>
        
        <div class="border-t pt-4">
          <label class="flex items-center gap-2">
            <input type="checkbox" 
                   data-field="active" 
                   ${outlet.active !== false ? 'checked' : ''}
                   data-action="change->outlet-flow-editor#updateField"
                   class="rounded border-gray-300">
            <span class="text-sm text-gray-700">Active</span>
          </label>
        </div>
        
        <div class="border-t pt-4">
          <h4 class="text-sm font-medium text-gray-700 mb-2">Chain Action (Optional)</h4>
          <p class="text-xs text-gray-500 mb-2">Execute another action when this outlet is triggered</p>
          <select data-field="chain_target_action_id"
                  data-action="change->outlet-flow-editor#updateField"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
            <option value="">No chain</option>
            ${(this.availableActionsValue || []).map(a => `
              <option value="${a.id}" ${outlet.chain?.target_action_id == a.id ? 'selected' : ''}>
                ${a.name}
              </option>
            `).join('')}
          </select>
        </div>
      </div>
    `
  }

  updateField(event) {
    if (!this.currentOutlet) return
    
    const field = event.target.dataset.field
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value
    
    // Update outlet in array
    const outlets = this.outletsValue || []
    this.outletsValue = outlets.map(o => {
      if (o.id === this.currentOutlet.id || o._tempId === this.currentOutlet._tempId) {
        const updated = { ...o, [field]: value }
        this.currentOutlet = updated
        return updated
      }
      return o
    })
    
    // Re-render nodes to reflect changes
    this.renderNodes()
    this.syncToForm()
  }

  updateOption(event) {
    if (!this.currentOutlet) return
    
    const index = parseInt(event.target.dataset.optionIndex)
    const field = event.target.dataset.optionField
    const value = event.target.value
    
    const outlets = this.outletsValue || []
    this.outletsValue = outlets.map(o => {
      if (o.id === this.currentOutlet.id || o._tempId === this.currentOutlet._tempId) {
        const options = [...(o.config?.options || [])]
        options[index] = { ...options[index], [field]: value }
        const updated = { ...o, config: { ...o.config, options } }
        this.currentOutlet = updated
        return updated
      }
      return o
    })
    
    this.syncToForm()
  }

  addOption() {
    if (!this.currentOutlet) return
    
    const outlets = this.outletsValue || []
    this.outletsValue = outlets.map(o => {
      if (o.id === this.currentOutlet.id || o._tempId === this.currentOutlet._tempId) {
        const options = [...(o.config?.options || []), { text: '', value: '' }]
        const updated = { ...o, config: { ...o.config, options } }
        this.currentOutlet = updated
        return updated
      }
      return o
    })
    
    // Re-render panel
    this.panelContentTarget.innerHTML = this.renderPanelContent(this.currentOutlet)
    this.syncToForm()
  }

  removeOption(event) {
    if (!this.currentOutlet) return
    
    const index = parseInt(event.currentTarget.dataset.index)
    
    const outlets = this.outletsValue || []
    this.outletsValue = outlets.map(o => {
      if (o.id === this.currentOutlet.id || o._tempId === this.currentOutlet._tempId) {
        const options = (o.config?.options || []).filter((_, i) => i !== index)
        const updated = { ...o, config: { ...o.config, options } }
        this.currentOutlet = updated
        return updated
      }
      return o
    })
    
    // Re-render panel
    this.panelContentTarget.innerHTML = this.renderPanelContent(this.currentOutlet)
    this.syncToForm()
  }

  // === Add Menu ===

  toggleAddMenu() {
    this.addMenuTarget.classList.toggle('hidden')
  }

  hideAddMenu() {
    this.addMenuTarget.classList.add('hidden')
  }

  hideEmptyState() {
    const emptyState = this.element.querySelector('[data-empty-state]')
    if (emptyState) {
      emptyState.classList.add('hidden')
    }
  }

  // === Form Sync ===

  syncToForm() {
    // Update hidden input with outlets JSON
    if (this.hasOutletsDataTarget) {
      this.outletsDataTarget.value = JSON.stringify(this.outletsValue || [])
    }
  }

  // === Resize handling ===

  handleResize() {
    if (this.view) {
      this.view.viewSize = new this.paperScope.Size(
        this.canvasTarget.offsetWidth,
        this.canvasTarget.offsetHeight
      )
      this.drawGrid()
      this.renderNodes()
    }
  }
}
